import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, Send, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t('contact.fillAll'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t('contact.successToast'));
    } catch (err) {
      toast.error(t('contact.errorToast'));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4EDE6]" data-testid="contact-sent">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-20 h-20 bg-[#C4A77D] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">
            {t('contact.successMsg')}
          </h1>
          <p className="text-[#595959]">
            {t('contact.successDesc')}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="contact-page">
      {/* Header */}
      <section className="relative py-20 overflow-hidden">
        <video 
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/contact-video.mp4"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="container-main text-center text-white relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-['Avenir_Next'] text-4xl md:text-5xl mb-4">
              {t('contact.heroTitle')}
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              {t('contact.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container-main py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-['Avenir_Next'] text-2xl text-[#1A1A1A] mb-6">
                {t('contact.infoTitle')}
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#C4A77D]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#C4A77D]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{t('contact.emailLabel')}</p>
                    <p className="text-[#595959]">info@vardagslyx.nu</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 space-y-6">
              <h2 className="font-['Avenir_Next'] text-2xl text-[#1A1A1A]">
                {t('contact.formTitle')}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider text-[#8C8C8C]">
                    {t('contact.name')} *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-12 rounded-xl border-[#C4A77D]/20"
                    placeholder={t('contact.namePlaceholder')}
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#8C8C8C]">
                    {t('contact.email')} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-12 rounded-xl border-[#C4A77D]/20"
                    placeholder={t('contact.emailPlaceholder')}
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject" className="text-xs uppercase tracking-wider text-[#8C8C8C]">
                  {t('contact.subject')} *
                </Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="h-12 rounded-xl border-[#C4A77D]/20"
                  placeholder={t('contact.subjectPlaceholder')}
                  data-testid="contact-subject"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-wider text-[#8C8C8C]">
                  {t('contact.message')} *
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="min-h-[150px] rounded-xl border-[#C4A77D]/20 resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary h-14 w-full md:w-auto px-12"
                data-testid="contact-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('contact.submit')}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
